import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import {
  ArrowRightIcon,
  CalendarDaysIcon,
  CreditCardIcon,
  DocumentTextIcon,
  InboxIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useBranding } from '../../context/BrandingContext';
import { customerPortalAPI } from '../../services/api';
import SavedPaymentMethodsPanel from '../../components/payments/SavedPaymentMethodsPanel';

type PortalRecord = {
  id: string;
  type: string;
  title: string;
  subtitle?: string | null;
  status?: string | null;
  lifecycleLabel?: string | null;
  eventDate?: string | null;
  amountLabel?: string | null;
  amountDueLabel?: string | null;
  href?: string | null;
  primaryActionLabel?: string | null;
  actions?: Record<string, boolean>;
};

const customerPortalHeroImage = '/login-hero-luxury-fleet.png';

function storageKey() {
  const host = typeof window !== 'undefined' ? window.location.host : 'local';
  return `modal_customer_portal:${host}`;
}

function readPortalSession(): { portalToken: string; email: string; expiresAt: string } | null {
  try {
    const raw = localStorage.getItem(storageKey());
    if (!raw) return null;
    const session = JSON.parse(raw);
    if (!session?.portalToken || !session?.expiresAt) return null;
    if (new Date(session.expiresAt).getTime() <= Date.now()) {
      localStorage.removeItem(storageKey());
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

function formatDate(value?: string | null) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function recordIcon(type: string) {
  if (type === 'reservation') return CalendarDaysIcon;
  if (type === 'payment_request' || type === 'invoice') return CreditCardIcon;
  if (type === 'quote') return DocumentTextIcon;
  return InboxIcon;
}

function RecordRow({
  record,
  onAcceptQuote,
  onDeclineQuote,
  busy,
}: {
  record: PortalRecord;
  onAcceptQuote: (record: PortalRecord) => void;
  onDeclineQuote: (record: PortalRecord) => void;
  busy: boolean;
}) {
  const Icon = recordIcon(record.type);
  const dateLabel = formatDate(record.eventDate);
  const canAccept = record.type === 'quote' && Boolean(record.actions?.accept);
  const canDecline = record.type === 'quote' && Boolean(record.actions?.decline);

  return (
    <div className="grid gap-4 border-b border-outline/60 py-4 last:border-b-0 sm:grid-cols-[1fr_auto] sm:items-center">
      <div className="flex min-w-0 gap-3">
        <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate text-sm font-semibold text-on-surface sm:text-base">
              {record.title}
            </h3>
            {record.lifecycleLabel ? (
              <span className="rounded-full bg-surface-container-low px-2.5 py-1 text-xs font-semibold text-on-surface-variant">
                {record.lifecycleLabel}
              </span>
            ) : null}
          </div>
          <p className="mt-1 text-sm leading-5 text-on-surface-variant">
            {[record.subtitle, dateLabel].filter(Boolean).join(' | ')}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 sm:justify-end">
        {record.amountDueLabel || record.amountLabel ? (
          <span className="rounded-full bg-surface-container-low px-3 py-2 text-sm font-semibold text-on-surface">
            {record.amountDueLabel || record.amountLabel}
          </span>
        ) : null}
        {canDecline ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => onDeclineQuote(record)}
            className="rounded-full border border-outline px-3 py-2 text-sm font-semibold text-on-surface disabled:opacity-60"
          >
            Decline
          </button>
        ) : null}
        {canAccept ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => onAcceptQuote(record)}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
          >
            Accept
          </button>
        ) : null}
        {record.href ? (
          <a
            href={record.href}
            className="inline-flex items-center gap-2 rounded-full border border-outline px-4 py-2 text-sm font-semibold text-primary"
          >
            {record.primaryActionLabel || 'Open'}
            <ArrowRightIcon className="h-4 w-4" />
          </a>
        ) : null}
      </div>
    </div>
  );
}

function RecordSection({
  title,
  description,
  records,
  emptyLabel,
  onAcceptQuote,
  onDeclineQuote,
  busy,
}: {
  title: string;
  description?: string;
  records: PortalRecord[];
  emptyLabel: string;
  onAcceptQuote: (record: PortalRecord) => void;
  onDeclineQuote: (record: PortalRecord) => void;
  busy: boolean;
}) {
  return (
    <section className="rounded-card border border-outline/60 bg-surface p-5 shadow-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-on-surface">{title}</h2>
          {description ? (
            <p className="mt-1 text-sm leading-5 text-on-surface-variant">{description}</p>
          ) : null}
        </div>
        <span className="rounded-full bg-surface-container-low px-3 py-1 text-xs font-semibold text-on-surface-variant">
          {records.length}
        </span>
      </div>
      <div className="mt-3">
        {records.length > 0 ? (
          records.map((record) => (
            <RecordRow
              key={`${record.type}-${record.id}`}
              record={record}
              onAcceptQuote={onAcceptQuote}
              onDeclineQuote={onDeclineQuote}
              busy={busy}
            />
          ))
        ) : (
          <div className="rounded-card border border-dashed border-outline/70 bg-surface-container-lowest p-6 text-sm text-on-surface-variant">
            {emptyLabel}
          </div>
        )}
      </div>
    </section>
  );
}

export default function CustomerPortalPage() {
  const branding = useBranding();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const session = useMemo(readPortalSession, []);
  const companyName = branding.app_name || 'ModalOS';

  const recordsQuery = useQuery(
    ['customer-portal-records', session?.email],
    async () => {
      const response = await customerPortalAPI.getRecords(session!.portalToken);
      return response.data;
    },
    {
      enabled: Boolean(session?.portalToken),
      retry: false,
      onError: () => {
        localStorage.removeItem(storageKey());
        navigate('/customer/access', { replace: true });
      },
    }
  );

  const acceptMutation = useMutation(
    async (record: PortalRecord) => {
      const response = await customerPortalAPI.acceptQuote(record.id, session!.portalToken);
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success('Quote accepted.');
        queryClient.invalidateQueries(['customer-portal-records', session?.email]);
        const nextUrl = data?.paymentUrl || data?.reservationPublicUrl;
        if (nextUrl) window.location.assign(nextUrl);
      },
      onError: () => {
        toast.error('We could not accept that quote.');
      },
    }
  );

  const declineMutation = useMutation(
    async (record: PortalRecord) => {
      const response = await customerPortalAPI.declineQuote(record.id, session!.portalToken);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Quote declined.');
        queryClient.invalidateQueries(['customer-portal-records', session?.email]);
      },
      onError: () => {
        toast.error('We could not decline that quote.');
      },
    }
  );

  if (!session) {
    return (
      <main className="min-h-screen bg-[#101724] px-4 py-10 text-white">
        <div className="mx-auto grid min-h-[78vh] max-w-5xl overflow-hidden rounded-card border border-white/15 bg-white/10 shadow-card lg:grid-cols-[1fr_420px]">
          <div className="relative hidden min-h-[520px] lg:block">
            <img
              src={customerPortalHeroImage}
              alt="Luxury vehicles parked outside a modern hotel"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-[#101724]/45" />
          </div>
          <div className="flex items-center p-6 sm:p-10">
            <div>
              {branding.logo_url ? (
                <img
                  src={branding.logo_url}
                  alt={companyName}
                  className="h-10 w-auto max-w-[8.5rem] rounded-sm bg-white/95 p-2 object-contain"
                />
              ) : (
                <div className="flex h-14 w-14 items-center justify-center rounded-md bg-white/10 text-lg font-semibold">
                  {companyName.slice(0, 1)}
                </div>
              )}
              <p className="mt-8 text-xs font-semibold uppercase text-white/60">
                Customer Action Center
              </p>
              <h1 className="mt-2 text-3xl font-semibold">Access your records</h1>
              <p className="mt-3 text-sm leading-6 text-white/70">
                Verify your email to view private quotes, reservations, invoices, and payment records for {companyName}.
              </p>
              <Link
                to="/customer/access"
                className="mt-6 inline-flex items-center justify-center rounded-full bg-white px-5 py-3 text-sm font-semibold text-[#101724]"
              >
                Continue securely
              </Link>
            </div>
          </div>
        </div>
      </main>
    );
  }

  const data = recordsQuery.data || {};
  const company = data.company || {};
  const displayName = company.appName || company.name || companyName;
  const primaryColor = company.primaryColor || branding.primary_color || '#2563eb';
  const needsAction = data.needsAction || [];
  const upcomingReservations = data.upcomingReservations || [];
  const payments = data.payments || [];
  const quotes = data.quotes || [];
  const history = data.history || [];
  const busy = acceptMutation.isLoading || declineMutation.isLoading;

  return (
    <main className="min-h-screen bg-surface pb-10">
      <header className="relative overflow-hidden bg-[#101724] text-white">
        <img
          src={customerPortalHeroImage}
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-[#101724]/82" />
        <div className="relative mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-center gap-4">
              {company.logoUrl || branding.logo_url ? (
                <img
                  src={company.logoUrl || branding.logo_url}
                  alt={displayName}
                  className="h-10 w-auto max-w-[8.5rem] rounded-sm bg-white/95 p-2 object-contain"
                />
              ) : (
                <div
                  className="flex h-14 w-14 items-center justify-center rounded-md text-lg font-semibold text-white"
                  style={{ backgroundColor: primaryColor }}
                >
                  {displayName.slice(0, 1)}
                </div>
              )}
              <div className="min-w-0">
                <p className="text-sm text-white/65">
                  Customer Action Center
                </p>
                <h1 className="truncate text-2xl font-semibold sm:text-3xl">
                  Access your records
                </h1>
                <p className="mt-1 text-sm text-white/65">
                  {displayName} · {session.email}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                localStorage.removeItem(storageKey());
                navigate('/customer/access', { replace: true });
              }}
              className="rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold text-white"
            >
              Switch email
            </button>
          </div>

          <div className="mt-10 grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
            <div>
              <p className="text-sm font-semibold uppercase text-white/55">
                Your trip records
              </p>
              <h2 className="mt-2 max-w-3xl text-3xl font-semibold leading-tight sm:text-4xl">
                A private place for trip details, quotes, and secure payments.
              </h2>
              <p className="mt-4 max-w-2xl text-base leading-7 text-white/70">
                Continue where you left off with active quotes, upcoming reservations, deposits, balances, and invoice records tied to your verified email.
              </p>
            </div>
            <div className="rounded-card border border-white/15 bg-white/10 p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10">
                  <ShieldCheckIcon className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm text-white/60">Needs action</p>
                  <p className="text-2xl font-semibold">{needsAction.length}</p>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 text-center text-xs text-white/70">
                <div className="rounded-md bg-white/10 px-2 py-2">
                  <p className="font-semibold text-white">{upcomingReservations.length}</p>
                  <p>Upcoming</p>
                </div>
                <div className="rounded-md bg-white/10 px-2 py-2">
                  <p className="font-semibold text-white">{quotes.length}</p>
                  <p>Quotes</p>
                </div>
                <div className="rounded-md bg-white/10 px-2 py-2">
                  <p className="font-semibold text-white">{payments.length}</p>
                  <p>Payments</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        </header>

      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        {recordsQuery.isLoading ? (
          <div className="mt-8 flex min-h-[40vh] items-center justify-center">
            <LoadingSpinner size="lg" />
          </div>
        ) : (
          <div className="mt-6 grid gap-5">
            <SavedPaymentMethodsPanel portalToken={session.portalToken} portalEmail={session.email} />
            <RecordSection
              title="Needs Action"
              description="Quotes, deposits, balances, and invoices that are ready for your response."
              records={needsAction}
              emptyLabel="Nothing needs action right now."
              onAcceptQuote={(record) => acceptMutation.mutate(record)}
              onDeclineQuote={(record) => declineMutation.mutate(record)}
              busy={busy}
            />
            <RecordSection
              title="Upcoming Reservations"
              records={upcomingReservations}
              emptyLabel="No upcoming reservations matched this email."
              onAcceptQuote={(record) => acceptMutation.mutate(record)}
              onDeclineQuote={(record) => declineMutation.mutate(record)}
              busy={busy}
            />
            <div className="grid gap-5 lg:grid-cols-2">
              <RecordSection
                title="Payments"
                records={payments}
                emptyLabel="No payment records matched this email."
                onAcceptQuote={(record) => acceptMutation.mutate(record)}
                onDeclineQuote={(record) => declineMutation.mutate(record)}
                busy={busy}
              />
              <RecordSection
                title="Quotes"
                records={quotes}
                emptyLabel="No quote records matched this email."
                onAcceptQuote={(record) => acceptMutation.mutate(record)}
                onDeclineQuote={(record) => declineMutation.mutate(record)}
                busy={busy}
              />
            </div>
            <RecordSection
              title="History"
              records={history}
              emptyLabel="No completed records matched this email yet."
              onAcceptQuote={(record) => acceptMutation.mutate(record)}
              onDeclineQuote={(record) => declineMutation.mutate(record)}
              busy={busy}
            />
          </div>
        )}
      </div>
    </main>
  );
}
